import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { Helmet } from 'react-helmet-async';
import { Calendar, ChevronLeft, Share2, Eye } from 'lucide-react';
import Footer from '../components/Footer';

const BlogPostPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [blog, setBlog] = useState(null);
    const [relatedBlogs, setRelatedBlogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBlogAndRelated = async () => {
            setLoading(true);

            // 1. Fetch Main Blog
            const { data: currentBlog, error } = await supabase
                .from('blogs')
                .select('*, category:blog_categories(id, name, slug)')
                .eq('slug', slug)
                .eq('status', 'published')
                .single();

            if (error || !currentBlog) {
                navigate('/blog', { replace: true });
                return;
            }

            setBlog(currentBlog);

            // Increment View Count (With 10 Minutes Cooldown per Blog)
            const VIEW_COOLDOWN = 10 * 60 * 1000; // 10 minutes
            const storageKey = 'blog_view_history';

            try {
                const history = JSON.parse(localStorage.getItem(storageKey) || '{}');
                const lastViewed = history[currentBlog.id];
                const now = Date.now();

                if (!lastViewed || (now - lastViewed > VIEW_COOLDOWN)) {
                    // Belum dilihat atau sudah lebih dari 10 menit -> Hitung!
                    await supabase.rpc('increment_blog_view_count', { blog_id: currentBlog.id });

                    // Update storage
                    history[currentBlog.id] = now;
                    localStorage.setItem(storageKey, JSON.stringify(history));
                    console.log("View count incremented (Cooldown reset)");
                } else {
                    console.log("View count skipped (Inside 10m cooldown)");
                }
            } catch (e) {
                console.error("View count logic error:", e);
                // Fallback: tetap hitung jika localstorage error
                supabase.rpc('increment_blog_view_count', { blog_id: currentBlog.id });
            }

            // 2. Fetch Related Blogs
            if (currentBlog.category_id) {
                const { data: relatedData } = await supabase
                    .from('blogs')
                    .select('*, category:blog_categories(name)')
                    .eq('status', 'published')
                    .eq('category_id', currentBlog.category_id)
                    .neq('id', currentBlog.id)
                    .order('published_at', { ascending: false })
                    .limit(5); // User requested 5 data
                setRelatedBlogs(relatedData || []);
            }

            setLoading(false);
        };

        fetchBlogAndRelated();
    }, [slug, navigate]);

    if (loading) return (
        <div className="min-h-screen bg-white flex items-center justify-center">
            <div className="text-slate-400 text-sm font-medium animate-pulse">Memuat artikel...</div>
        </div>
    );

    if (!blog) return null;

    return (
        <div className="min-h-screen bg-slate-50/50 flex flex-col font-sans">
            <Helmet>
                <title>{blog.meta_title || blog.title} - Neo AI Blog</title>
                <meta name="description" content={blog.meta_description || blog.excerpt} />

                {/* Structured Data for SEO */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BlogPosting",
                        "headline": blog.title,
                        "description": blog.meta_description || blog.excerpt,
                        "image": blog.featured_image ? [blog.featured_image] : [],
                        "datePublished": blog.published_at || blog.created_at,
                        "dateModified": blog.updated_at || blog.published_at || blog.created_at,
                        "author": {
                            "@type": "Organization",
                            "name": "NeoAI"
                        },
                        "publisher": {
                            "@type": "Organization",
                            "name": "NeoAI",
                            "logo": {
                                "@type": "ImageObject",
                                "url": "https://neoai.id/icon.svg"
                            }
                        },
                        "mainEntityOfPage": {
                            "@type": "WebPage",
                            "@id": `https://neoai.id/blog/${blog.slug}`
                        }
                    })}
                </script>
            </Helmet>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full py-6 flex-1 flex flex-col">

                {/* BACK BUTTON */}
                <div className="mb-4 shrink-0">
                    <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-sky-600 transition-colors">
                        <ChevronLeft size={16} /> Kembali ke Blog
                    </Link>
                </div>

                <div className="flex flex-col lg:grid lg:grid-cols-12 gap-8 lg:gap-10 pb-10">

                    {/* RIGHT CONTENT (Main Article) 
                         - Fixed height to ensure internal scrolling
                    */}
                    <div className="order-1 lg:order-2 lg:col-span-8 h-[80vh] lg:h-[85vh] overflow-y-auto custom-scrollbar flex flex-col gap-10">
                        <main className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5 md:p-10">

                            {/* COVER IMAGE (Visible on all screens) */}
                            {blog.featured_image && (
                                <div className="rounded-2xl overflow-hidden shadow-sm border border-slate-100 aspect-video mb-8">
                                    <img
                                        src={blog.featured_image}
                                        alt={blog.title}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            )}

                            {/* Title & Date */}
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 md:gap-6 border-b border-slate-100 pb-6 mb-8">
                                <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 leading-tight text-left flex-1 tracking-tight">
                                    {blog.title}
                                </h1>
                                <div className="flex flex-col sm:flex-row gap-3 sm:items-center text-sm font-semibold text-slate-500 shrink-0 pt-1">
                                    <div className="flex items-center gap-2">
                                        <div className="p-2 bg-sky-50 rounded-lg text-sky-600">
                                            <Calendar size={18} />
                                        </div>
                                        <span className="whitespace-nowrap">
                                            {new Date(blog.published_at || blog.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
                                        </span>
                                    </div>
                                    <div className="hidden sm:block text-slate-300">|</div>
                                    <div className="flex items-center gap-2">
                                        <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600">
                                            <Eye size={18} />
                                        </div>
                                        <span className="whitespace-nowrap">
                                            {(blog.view_count || 0).toLocaleString('id-ID')} Views
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Content */}
                            <div
                                className="
                                    text-slate-700 leading-relaxed font-normal text-[17px]
                                    [&_p]:mb-6 [&_p]:leading-8
                                    [&_h1]:text-3xl [&_h1]:font-black [&_h1]:text-slate-900 [&_h1]:mt-10 [&_h1]:mb-6 [&_h1]:leading-tight
                                    [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:leading-snug
                                    [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-slate-900 [&_h3]:mt-8 [&_h3]:mb-3
                                    [&_h4]:text-lg [&_h4]:font-bold [&_h4]:text-slate-900 [&_h4]:mt-6 [&_h4]:mb-2
                                    [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6 [&_ul]:space-y-2
                                    [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-6 [&_ol]:space-y-2
                                    [&_li]:pl-1
                                    [&_blockquote]:border-l-4 [&_blockquote]:border-sky-500 [&_blockquote]:pl-5 [&_blockquote]:italic [&_blockquote]:text-slate-600 [&_blockquote]:my-8 [&_blockquote]:bg-slate-50 [&_blockquote]:py-4 [&_blockquote]:pr-4 [&_blockquote]:rounded-r-lg
                                    [&_a]:text-sky-600 [&_a]:font-medium [&_a]:underline hover:[&_a]:text-sky-700
                                    [&_img]:rounded-2xl [&_img]:shadow-md [&_img]:my-8 [&_img]:w-full [&_img]:object-cover
                                    [&_strong]:font-bold [&_strong]:text-slate-900
                                    [&_pre]:bg-slate-900 [&_pre]:text-slate-50 [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre]:my-6
                                    [&_code]:bg-slate-100 [&_code]:text-pink-600 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono
                                "
                                dangerouslySetInnerHTML={{ __html: blog.content }}
                            />

                            {/* Footer Share */}
                            <div className="mt-12 pt-8 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
                                <p className="text-slate-500 text-sm font-medium">
                                    Bagikan artikel ini:
                                </p>
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(window.location.href);
                                        alert('Link tersalin!');
                                    }}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-50 text-slate-600 hover:text-sky-600 hover:bg-sky-50 transition text-sm font-bold"
                                >
                                    <Share2 size={16} /> Salin Link
                                </button>
                            </div>
                        </main>
                    </div>

                    {/* LEFT SIDEBAR (Recommendations)
                        Mobile: Order 2 (Last)
                        Desktop: Order 1 (Left)
                         - Fixed height to ensure internal scrolling
                    */}
                    <aside className="order-2 lg:order-1 lg:col-span-4 h-[80vh] lg:h-[85vh] overflow-y-auto custom-scrollbar flex flex-col gap-6">

                        {/* Recommendations */}
                        <div className="flex-1 bg-white rounded-2xl border border-slate-100 shadow-sm flex flex-col overflow-hidden min-h-0 h-96 lg:h-auto">
                            <div className="p-4 border-b border-slate-100 bg-white sticky top-0 z-10 flex items-center justify-between shadow-sm">
                                <h3 className="font-bold text-slate-900 flex items-center gap-2">
                                    <span className="w-1 h-4 bg-sky-500 rounded-full"></span>
                                    Rekomendasi
                                </h3>
                            </div>

                            <div className="overflow-y-auto p-4 space-y-5 hover:scroll-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                                {relatedBlogs.length > 0 ? relatedBlogs.map(item => (
                                    <Link key={item.id} to={`/blog/${item.slug}`} className="flex flex-col gap-3 group border-b border-slate-50 last:border-0 pb-4 last:pb-0">
                                        <div className="flex gap-4">
                                            <div className="w-24 h-16 shrink-0 rounded-lg overflow-hidden bg-slate-100 relative shadow-sm">
                                                {item.featured_image && (
                                                    <img src={item.featured_image} alt={item.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0 flex flex-col justify-center">
                                                <h4 className="font-bold text-slate-800 text-sm leading-snug group-hover:text-sky-600 transition-colors line-clamp-2 mb-1.5">
                                                    {item.title}
                                                </h4>
                                                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full w-fit">
                                                    {new Date(item.published_at || item.created_at).toLocaleDateString('id-ID', { year: 'numeric', month: 'short', day: 'numeric' })}
                                                </span>
                                            </div>
                                        </div>
                                        <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">
                                            {item.excerpt || (item.content ? item.content.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').substring(0, 80) + '...' : '')}
                                        </p>
                                    </Link>
                                )) : (
                                    <p className="text-center text-sm text-slate-400 py-4">Belum ada artikel terkait.</p>
                                )}
                            </div>
                        </div>
                    </aside>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default BlogPostPage;
